import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Skeleton,
  Typography,
  Alert,
} from '@mui/material';
import {
  useDataProvider,
  useNotify,
  useRefresh,
  useRedirect,
} from 'react-admin';
import { DropResult } from '@hello-pangea/dnd';
import { SharedKanbanBoard, KanbanStage } from '../../../components/shared/KanbanBoard';
import { KanbanCard } from '../../../components/shared/KanbanCard';
import type { TaskFilters } from '../types';
import type { Workflow, TaskStatus as WorkflowStatus } from '../../../types/workflow.types';

interface TaskBoardViewProps {
  filters: TaskFilters;
  selectedWorkflow?: Workflow;
  onClearFilters?: () => void;
}

interface TaskWithDetails {
  id: string;
  title: string;
  description?: string;
  workflowId: string;
  statusId: string;
  status?: WorkflowStatus;
  priority: string;
  urgency?: string;
  sortOrder: number;
  categoryId?: string;
  assigneeIds?: string[];
  assignees?: {
    id: string;
    name: string;
    avatar?: string;
  }[];
  assigneeId?: string;
  assignee?: {
    id: number;
    name: string;
    email: string;
  };
  category?: {
    id: string;
    name: string;
    color: string;
  };
  dueDate?: string;
  scheduledDate?: string;
  customer?: {
    id: string;
    displayName: string;
    company?: string;
  };
  customerId?: string;
  tags?: string[];
  recurrenceFrequency?: string;
  recurrenceEndDate?: string;
}

import { isVirtualTask } from '../../../utils/taskUtils';

export const TaskBoardView: React.FC<TaskBoardViewProps> = ({ filters, selectedWorkflow }) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();

  const [loading, setLoading] = useState(true);
  const [tasksByStatus, setTasksByStatus] = useState<Record<string, TaskWithDetails[]>>({});

  // Define loadTasks function outside useEffect so it can be reused
  const loadTasks = useCallback(async () => {
    if (!selectedWorkflow) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      // Build filter
      const taskFilter: any = {
        workflowId: selectedWorkflow.id,
      };

      if (filters.search) taskFilter.q = filters.search;
      if (filters.customerId) taskFilter.customerId = filters.customerId;
      if (filters.zoneId) taskFilter.zoneId = filters.zoneId;
      if (filters.categoryId) taskFilter.categoryId = filters.categoryId;
      if (filters.statusId && filters.statusId.length > 0) {
        taskFilter.statusId = filters.statusId;
      }
      if (filters.assigneeId) taskFilter.assigneeId = filters.assigneeId;
      if (filters.priority && filters.priority.length > 0) {
        taskFilter.priority = filters.priority;
      }
      if (filters.dateRange?.start) {
        taskFilter.startDate = filters.dateRange.start.toISOString();
      }
      if (filters.dateRange?.end) {
        taskFilter.endDate = filters.dateRange.end.toISOString();
      }

      const { data: tasks } = await dataProvider.getList('tasks', {
        pagination: { page: 1, perPage: 500 },
        sort: { field: 'sortOrder', order: 'ASC' },
        filter: taskFilter,
      });

      // Group tasks by status
      const grouped: Record<string, TaskWithDetails[]> = {};

      // Initialize with all workflow statuses
      selectedWorkflow.statuses.forEach(status => {
        grouped[status.id] = [];
      });

      // Add tasks to their statuses
      tasks.forEach((task: TaskWithDetails) => {
        const statusId = task.statusId;
        if (grouped[statusId]) {
          grouped[statusId].push(task);
        }
      });

      // Sort tasks within each status by sortOrder
      Object.keys(grouped).forEach(statusId => {
        grouped[statusId].sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
      });

      setTasksByStatus(grouped);
    } catch (error) {
      notify('Failed to load tasks', { type: 'error' });
      console.error('Failed to load tasks:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedWorkflow, filters, dataProvider, notify]);

  // Load tasks when component mounts or dependencies change
  useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  const calculateNewSortOrder = (
    tasks: TaskWithDetails[],
    destinationIndex: number
  ): number => {
    const INITIAL_GAP = 1000;
    const sortedTasks = [...tasks].sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));

    if (destinationIndex === 0) {
      return (sortedTasks[0]?.sortOrder ?? INITIAL_GAP) / 2;
    }

    if (destinationIndex >= sortedTasks.length) {
      return (sortedTasks[sortedTasks.length - 1]?.sortOrder ?? 0) + INITIAL_GAP;
    }

    const prevOrder = sortedTasks[destinationIndex - 1].sortOrder ?? 0;
    const nextOrder = sortedTasks[destinationIndex].sortOrder ?? prevOrder + INITIAL_GAP;
    return (prevOrder + nextOrder) / 2;
  };

  const handleDragEnd = async (result: DropResult) => {
    const { destination, source, draggableId } = result;

    if (!destination) {
      return;
    }
    if (destination.droppableId === source.droppableId && destination.index === source.index) {
      return;
    }

    const sourceStatusId = source.droppableId;
    const destinationStatusId = destination.droppableId;
    const taskId = draggableId;

    // Find the task being moved
    const sourceTask = tasksByStatus[sourceStatusId]?.find(
      task => task.id === taskId
    );
    if (!sourceTask) {
      return;
    }

    // Calculate new sort order
    const destinationTasks = tasksByStatus[destinationStatusId] || [];
    const newSortOrder = calculateNewSortOrder(destinationTasks, destination.index);

    // Update local state optimistically
    const newTasksByStatus = { ...tasksByStatus };

    // Remove from source
    newTasksByStatus[sourceStatusId] = newTasksByStatus[sourceStatusId].filter(
      task => task.id !== taskId
    );

    // Add to destination
    if (!newTasksByStatus[destinationStatusId]) {
      newTasksByStatus[destinationStatusId] = [];
    }

    const updatedTask = {
      ...sourceTask,
      statusId: destinationStatusId,
      sortOrder: newSortOrder,
    };

    newTasksByStatus[destinationStatusId].splice(destination.index, 0, updatedTask);

    setTasksByStatus(newTasksByStatus);

    try {
      // Update via API
      await dataProvider.update('tasks', {
        id: taskId,
        data: {
          statusId: destinationStatusId,
          sortOrder: newSortOrder,
        },
        previousData: sourceTask,
      });

      notify('Task moved successfully', { type: 'success' });
    } catch (error) {
      notify('Failed to move task', { type: 'error' });
      console.error('Failed to update task:', error);
      // Revert on error
      refresh();
    }
  };

  const redirect = useRedirect();

  const handleTaskClick = (task: any) => {
    redirect('show', 'tasks', task.id);
  };

  const renderTaskCard = (task: TaskWithDetails, index: number) => (
    <KanbanCard
      key={task.id}
      item={{
        ...task,
        title: task.title,
        dueDate: task.dueDate || task.scheduledDate,
        assignedUsers: task.assignees,
        assignedUser: task.assignee ? {
          id: task.assignee.id,
          name: task.assignee.name,
        } : undefined,
        customer: task.customer ? {
          id: task.customer.id,
          displayName: task.customer.displayName || task.customer.company || '',
        } : task.customerId ? {
          id: task.customerId,
          displayName: `Customer #${task.customerId}`,
        } : undefined,
        description: task.description,
        priority: task.priority,
        category: task.category,
        recurrenceFrequency: task.recurrenceFrequency,
        recurrenceEndDate: task.recurrenceEndDate,
        isVirtual: isVirtualTask(task),
      }}
      index={index}
      onClick={handleTaskClick}
      showValue={false}
      showProbability={false}
      showDueDate={true}
      showCustomer={true}
      showAssignedUser={true}
      showPriority={true}
      showCategory={true}
    />
  );

  if (loading) {
    return (
      <Box sx={{ p: 2 }}>
        <Skeleton variant="rectangular" height={400} />
      </Box>
    );
  }

  if (!selectedWorkflow) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">
          Please select a workflow to view the task board
        </Alert>
      </Box>
    );
  }

  // Convert workflow statuses to Kanban stages
  const kanbanStages: KanbanStage[] = selectedWorkflow.statuses
    .sort((a, b) => a.orderIndex - b.orderIndex)
    .map(status => ({
      id: status.id,
      name: status.name,
      color: status.color,
    }));

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <SharedKanbanBoard
        stages={kanbanStages}
        itemsByStage={tasksByStatus}
        onDragEnd={handleDragEnd}
        renderCard={renderTaskCard}
        itemCountLabel="tasks"
        emptyStateMessage="No tasks in this status"
        emptyStateSubtext="Drag tasks here or create a new task"
        loading={loading}
      />
    </Box>
  );
};